import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  ArrowRight, 
  CheckCircle, 
  Shield, 
  Building, 
  FileText,
  Sparkles,
  AlertCircle,
  Loader2,
  FolderOpen,
  Check,
  Lightbulb,
  Server,
  Search,
  ChevronDown,
  ChevronRight,
  Zap,
  Clock
} from 'lucide-react';

// Using relative URLs
const API = '/api';

// Lightweight self-ticking elapsed timer (mm:ss) shown during generation so
// the screen always has visible motion even while the AI is thinking.
const ElapsedTimer = ({ startedAt }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!startedAt) return null;
  const secs = Math.max(0, Math.floor((now - startedAt) / 1000));
  const mm = String(Math.floor(secs / 60)).padStart(2, '0');
  const ss = String(secs % 60).padStart(2, '0');
  return (
    <span className="inline-flex items-center gap-1.5 text-sm text-slate-500" data-testid="wizard-elapsed-timer">
      <Clock className="w-3.5 h-3.5" />
      {mm}:{ss} elapsed
    </span>
  );
};

const FRAMEWORKS = [
  {
    id: 'SOC 2',
    name: 'SOC 2',
    description: 'Service Organization Control 2 - Security, availability, processing integrity, confidentiality, and privacy',
    color: 'border-blue-200 bg-blue-50 text-blue-700'
  },
  {
    id: 'GDPR',
    name: 'GDPR',
    description: 'General Data Protection Regulation - EU data protection and privacy regulation',
    color: 'border-red-200 bg-red-50 text-red-700'
  },
  {
    id: 'ISO 27001',
    name: 'ISO 27001',
    description: 'International standard for information security management systems',
    color: 'border-purple-200 bg-purple-50 text-purple-700'
  },
  {
    id: 'PCI DSS',
    name: 'PCI DSS',
    description: 'Payment Card Industry Data Security Standard - Secure payment card data processing',
    color: 'border-orange-200 bg-orange-50 text-orange-700'
  },
  {
    id: 'HIPAA',
    name: 'HIPAA',
    description: 'Health Insurance Portability and Accountability Act - US healthcare data protection',
    color: 'border-teal-200 bg-teal-50 text-teal-700'
  }
];

const AuditWizard = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    organization: '',
    frameworks: [],
    technologies: [] // Selected technology IDs
  });
  
  // Technology state
  const [technologyCategories, setTechnologyCategories] = useState([]);
  const [loadingTechnologies, setLoadingTechnologies] = useState(false);
  const [techSearchTerm, setTechSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});
  
  // New state for progressive control generation
  const [auditId, setAuditId] = useState(null);
  const [generationProgress, setGenerationProgress] = useState({
    phase: 'idle',
    plan: null,
    categories: [],
    currentCategory: null,
    currentCategoryIndex: 0,
    totalCategories: 0,
    processedCategories: [],
    categoryResults: {},
    totalControlsGenerated: 0,
    promptsUsed: [],
    dedupElapsed: 0
  });
  const [genStartedAt, setGenStartedAt] = useState(null);

  const totalSteps = 5; // Now 5 steps including technologies and generation
  
  // Fetch technologies when reaching step 3
  useEffect(() => {
    if (currentStep === 3) {
      fetchTechnologies();
    }
  }, [currentStep]);
  
  const fetchTechnologies = async () => {
    setLoadingTechnologies(true);
    try {
      const response = await axios.get(`${API}/technologies/all`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setTechnologyCategories(response.data);
      // Expand all categories by default
      const expanded = {};
      response.data.forEach(cat => {
        expanded[cat.id] = true;
      });
      setExpandedCategories(expanded);
    } catch (error) {
      console.error('Failed to load technologies:', error);
      // Don't show error - technologies are optional
    } finally {
      setLoadingTechnologies(false);
    }
  };
  
  const handleTechnologyToggle = (techId) => {
    setFormData(prev => ({
      ...prev,
      technologies: prev.technologies.includes(techId)
        ? prev.technologies.filter(t => t !== techId)
        : [...prev.technologies, techId]
    }));
  };
  
  const toggleCategoryExpanded = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };
  
  const selectAllInCategory = (categoryId) => {
    const category = technologyCategories.find(c => c.id === categoryId);
    if (!category) return;
    
    const categoryTechIds = category.technologies.map(t => t.id);
    const allSelected = categoryTechIds.every(id => formData.technologies.includes(id));
    
    if (allSelected) {
      // Deselect all in category
      setFormData(prev => ({
        ...prev,
        technologies: prev.technologies.filter(id => !categoryTechIds.includes(id))
      }));
    } else {
      // Select all in category
      setFormData(prev => ({
        ...prev,
        technologies: [...new Set([...prev.technologies, ...categoryTechIds])]
      }));
    }
  };
  
  // Filter technologies based on search
  const filteredCategories = technologyCategories.map(category => ({
    ...category,
    technologies: category.technologies.filter(tech =>
      tech.name.toLowerCase().includes(techSearchTerm.toLowerCase())
    )
  })).filter(category => category.technologies.length > 0 || !techSearchTerm);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFrameworkToggle = (frameworkId) => {
    setFormData(prev => ({
      ...prev,
      frameworks: prev.frameworks.includes(frameworkId)
        ? prev.frameworks.filter(f => f !== frameworkId)
        : [...prev.frameworks, frameworkId]
    }));
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.name.trim().length > 0;
      case 2:
        return formData.frameworks.length > 0;
      case 3:
        return true; // Technologies step, optional - always can proceed
      case 4:
        return true; // Review step, always can proceed
      case 5:
        return generationProgress.phase === 'complete'; // Generation step
      default:
        return false;
    }
  };

  const handleSubmit = async () => {
    if (!canProceed()) {
      toast.error('Please complete all required fields');
      return;
    }

    // Move to step 5 (generation step) and start the process
    setCurrentStep(5);
    await startControlGeneration();
  };
  
  const startControlGeneration = async () => {
    setLoading(true);
    setGenerationProgress({
      phase: 'creating_audit',
      plan: null,
      categories: [],
      currentCategory: null,
      currentCategoryIndex: 0,
      totalCategories: 0,
      processedCategories: [],
      categoryResults: {},
      totalControlsGenerated: 0
    });
    
    try {
      // Step 1: Create the audit (include technologies)
      const auditPayload = {
        ...formData,
        technologies: formData.technologies.length > 0 ? formData.technologies : null
      };
      const auditResponse = await axios.post(`${API}/audits`, auditPayload);
      const newAuditId = auditResponse.data.id;
      setAuditId(newAuditId);
      
      setGenerationProgress(prev => ({ ...prev, phase: 'planning' }));
      setGenStartedAt(Date.now());

      // Step 2: Kick off the whole generation pipeline as a background job.
      // Every request below returns in well under a second, so the deployment
      // edge/proxy never hits its request timeout (previously caused 520s).
      const startResp = await axios.post(
        `${API}/audits/${newAuditId}/generate-async`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }, timeout: 30000 }
      );
      const jobId = startResp.data.job_id;

      // Step 3: Poll for progress until the job completes or errors.
      const poll = async () => {
        const { data: job } = await axios.get(
          `${API}/audits/${newAuditId}/generation-status`,
          {
            params: { job_id: jobId },
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            timeout: 30000,
          }
        );
        setGenerationProgress(prev => ({
          ...prev,
          phase: job.phase || prev.phase,
          plan: job.categories ? { categories: job.categories } : prev.plan,
          categories: job.categories || prev.categories,
          promptsUsed: job.prompts_used || prev.promptsUsed,
          totalCategories: job.total_categories ?? prev.totalCategories,
          currentCategory: job.current_category ?? null,
          processedCategories: job.processed_names || prev.processedCategories,
          categoryResults: job.category_results || prev.categoryResults,
          totalControlsGenerated: job.total_controls ?? prev.totalControlsGenerated,
          dedupElapsed: job.dedup_elapsed ?? prev.dedupElapsed,
        }));
        return job;
      };

      // Poll every 2s; give the job plenty of time (large frameworks can take
      // several minutes). Each poll is a sub-second request.
      const maxPolls = 900; // ~30 minutes safety ceiling
      let job = null;
      for (let i = 0; i < maxPolls; i++) {
        try {
          job = await poll();
        } catch (pollErr) {
          // Transient network/edge hiccup — keep polling, state is server-side.
          await new Promise(r => setTimeout(r, 2000));
          continue;
        }
        if (job.status === 'complete') {
          setGenerationProgress(prev => ({ ...prev, phase: 'complete' }));
          toast.success(`Audit created with ${job.total_controls || 0} controls!`);
          break;
        }
        if (job.status === 'error') {
          throw new Error(job.error || 'Generation failed');
        }
        await new Promise(r => setTimeout(r, 2000));
      }
      if (!job || (job.status !== 'complete' && job.status !== 'error')) {
        throw new Error('Generation timed out. Please check the audit and try again.');
      }

    } catch (error) {
      console.error('Generation error:', error);
      setGenerationProgress(prev => ({ ...prev, phase: 'error' }));
      toast.error(error.response?.data?.detail || 'Failed to create audit');
    } finally {
      setLoading(false);
    }
  };
  
  const goToAudit = () => {
    if (auditId) {
      navigate(`/audit/${auditId}`);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6 animate-fadeIn">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-10 h-10 text-navy-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Audit Information</h2>
              <p className="text-slate-600">Start by gathering basic information about your audit</p>
            </div>

            <div className="space-y-6">
              <div>
                <Label htmlFor="name" className="text-sm font-medium text-slate-700 mb-2 block">
                  Audit Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., Q1 2024 SOC 2 Compliance Audit"
                  className="h-12 text-base"
                  data-testid="audit-name-input"
                />
                <p className="text-xs text-slate-500 mt-2">
                  Choose a descriptive name that identifies the audit purpose and timeframe
                </p>
              </div>

              <div>
                <Label htmlFor="organization" className="text-sm font-medium text-slate-700 mb-2 block">
                  Organization Name
                </Label>
                <Input
                  id="organization"
                  name="organization"
                  value={formData.organization}
                  onChange={handleInputChange}
                  placeholder="e.g., TechCorp Inc."
                  className="h-12 text-base"
                  data-testid="organization-input"
                />
                <p className="text-xs text-slate-500 mt-2">
                  Optional: This helps AI generate more relevant controls
                </p>
              </div>

              <div>
                <Label htmlFor="description" className="text-sm font-medium text-slate-700 mb-2 block">
                  Description
                </Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Describe the scope, objectives, and any specific requirements for this audit..."
                  rows={4}
                  className="text-base resize-none"
                  data-testid="description-input"
                />
                <p className="text-xs text-slate-500 mt-2">
                  Optional: Provide additional context about your audit scope and goals
                </p>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6 animate-fadeIn">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-navy-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-10 h-10 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Compliance Frameworks</h2>
              <p className="text-slate-600">Select the compliance frameworks you need to audit against</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {FRAMEWORKS.map((framework) => (
                <Card 
                  key={framework.id}
                  className={`cursor-pointer transition-all duration-200 hover:shadow-lg ${
                    formData.frameworks.includes(framework.id)
                      ? 'ring-2 ring-navy-500 border-navy-500 bg-emerald-50'
                      : 'hover:border-slate-300'
                  }`}
                  onClick={() => handleFrameworkToggle(framework.id)}
                  data-testid={`framework-${framework.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <Checkbox 
                        checked={formData.frameworks.includes(framework.id)}
                        onChange={() => handleFrameworkToggle(framework.id)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium border ${framework.color}`}>
                            {framework.name}
                          </span>
                          {formData.frameworks.includes(framework.id) && (
                            <CheckCircle className="w-5 h-5 text-navy-600" />
                          )}
                        </div>
                        <p className="text-sm text-slate-600 leading-relaxed">
                          {framework.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {formData.frameworks.length === 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Select at least one framework</p>
                  <p className="text-sm text-amber-700 mt-1">
                    You need to select at least one compliance framework to continue.
                  </p>
                </div>
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-6 animate-fadeIn">
            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-purple-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Server className="w-10 h-10 text-purple-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Technologies in Use</h2>
              <p className="text-slate-600">Select the technologies your organization uses to enable automation and better control mapping</p>
            </div>
            
            {/* Search */}
            <div className="relative max-w-md mx-auto">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search technologies..."
                value={techSearchTerm}
                onChange={(e) => setTechSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="tech-search-input"
              />
            </div>
            
            {loadingTechnologies ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                <span className="ml-2 text-slate-500">Loading technologies...</span>
              </div>
            ) : filteredCategories.length === 0 ? (
              <div className="text-center py-8 bg-slate-50 rounded-lg border border-slate-200">
                <Server className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-600 font-medium">No technologies configured yet</p>
                <p className="text-sm text-slate-500 mt-1">
                  Your admin can add technologies in the Super Admin panel
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredCategories.map((category) => {
                  const categoryTechIds = category.technologies.map(t => t.id);
                  const selectedCount = categoryTechIds.filter(id => formData.technologies.includes(id)).length;
                  const allSelected = categoryTechIds.length > 0 && selectedCount === categoryTechIds.length;
                  
                  return (
                    <Card key={category.id} className="border-slate-200">
                      <div 
                        className="flex items-center justify-between p-3 cursor-pointer hover:bg-slate-50"
                        onClick={() => toggleCategoryExpanded(category.id)}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-xl">{category.emoji || '📦'}</span>
                          <span className="font-medium text-slate-700">{category.name}</span>
                          <Badge variant="outline" className="text-xs">
                            {selectedCount}/{category.technologies.length} selected
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              selectAllInCategory(category.id);
                            }}
                            className="text-xs h-7"
                          >
                            {allSelected ? 'Deselect All' : 'Select All'}
                          </Button>
                          {expandedCategories[category.id] ? (
                            <ChevronDown className="w-4 h-4 text-slate-400" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-slate-400" />
                          )}
                        </div>
                      </div>
                      
                      {expandedCategories[category.id] && category.technologies.length > 0 && (
                        <CardContent className="pt-0 pb-3 px-3">
                          <div className="flex flex-wrap gap-2 pl-8">
                            {category.technologies.map((tech) => {
                              const isSelected = formData.technologies.includes(tech.id);
                              return (
                                <button
                                  key={tech.id}
                                  onClick={() => handleTechnologyToggle(tech.id)}
                                  className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                                    isSelected
                                      ? 'bg-purple-100 text-purple-700 border-2 border-purple-400'
                                      : 'bg-slate-100 text-slate-600 border-2 border-transparent hover:border-slate-300'
                                  }`}
                                  data-testid={`tech-${tech.id}`}
                                >
                                  {isSelected && <Check className="w-3 h-3 inline mr-1" />}
                                  {tech.name}
                                </button>
                              );
                            })}
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
            
            {/* Selected Summary */}
            {formData.technologies.length > 0 && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Zap className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-purple-800">
                      {formData.technologies.length} technolog{formData.technologies.length === 1 ? 'y' : 'ies'} selected
                    </p>
                    <p className="text-sm text-purple-700 mt-1">
                      AI will map controls to these technologies and identify automation opportunities
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {formData.technologies.length === 0 && !loadingTechnologies && filteredCategories.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">No technologies selected</p>
                    <p className="text-sm text-amber-700 mt-1">
                      You can skip this step, but selecting technologies helps identify automation opportunities
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 4:
        return (
          <div className="space-y-6 animate-fadeIn">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-10 h-10 text-navy-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Review & Create</h2>
              <p className="text-slate-600">Review your audit configuration before creating</p>
            </div>

            <Card className="bg-slate-50 border-slate-200">
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Audit Name</Label>
                    <p className="text-slate-900 mt-1 font-medium">{formData.name}</p>
                  </div>

                  {formData.organization && (
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Organization</Label>
                      <p className="text-slate-900 mt-1">{formData.organization}</p>
                    </div>
                  )}

                  {formData.description && (
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Description</Label>
                      <p className="text-slate-900 mt-1 leading-relaxed">{formData.description}</p>
                    </div>
                  )}

                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-3 block">
                      Selected Frameworks ({formData.frameworks.length})
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {formData.frameworks.map((frameworkId) => {
                        const framework = FRAMEWORKS.find(f => f.id === frameworkId);
                        return (
                          <span 
                            key={frameworkId}
                            className={`px-3 py-1 rounded-full text-sm font-medium border ${framework?.color}`}
                          >
                            {framework?.name}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Technologies Summary */}
                  {formData.technologies.length > 0 && (
                    <div>
                      <Label className="text-sm font-medium text-slate-700 mb-3 block">
                        Selected Technologies ({formData.technologies.length})
                      </Label>
                      <div className="flex flex-wrap gap-2">
                        {formData.technologies.map((techId) => {
                          // Find the technology name from categories
                          let techName = 'Unknown';
                          technologyCategories.forEach(cat => {
                            const tech = cat.technologies.find(t => t.id === techId);
                            if (tech) techName = tech.name;
                          });
                          return (
                            <span 
                              key={techId}
                              className="px-3 py-1 rounded-full text-sm font-medium border border-purple-200 bg-purple-50 text-purple-700"
                            >
                              {techName}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Sparkles className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-800">AI-Powered Control Generation</p>
                  <p className="text-sm text-blue-700 mt-1">
                    Click &quot;Create Audit&quot; to start. Our AI will generate controls category by category 
                    with real-time progress updates.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6 animate-fadeIn">
            {/* Creating Audit Phase */}
            {generationProgress.phase === 'creating_audit' && (
              <div className="text-center py-8">
                <div className="w-20 h-20 bg-navy-50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Loader2 className="w-10 h-10 text-navy-600 animate-spin" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Creating Audit</h2>
                <p className="text-slate-600">Setting up your new audit...</p>
              </div>
            )}
            
            {/* Planning Phase */}
            {generationProgress.phase === 'planning' && (
              <div className="text-center py-8">
                <div className="w-20 h-20 bg-purple-50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Lightbulb className="w-10 h-10 text-purple-600 animate-pulse" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Planning Control Categories</h2>
                <p className="text-slate-600">AI is identifying all the categories needed for your {formData.frameworks.join(', ')} audit...</p>
              </div>
            )}
            
            {/* Generating Phase - With Progress */}
            {generationProgress.phase === 'generating' && (
              <div className="py-4">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Sparkles className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">Generating Controls</h2>
                  <p className="text-slate-600">AI is creating controls for each category</p>
                  <div className="mt-2 flex items-center justify-center gap-3">
                    <ElapsedTimer startedAt={genStartedAt} />
                  </div>
                  <p className="text-xs text-slate-400 mt-2">This usually takes 1–3 minutes. You can keep this tab open.</p>
                </div>
                
                {/* Prompt Template Info */}
                {generationProgress.promptsUsed && generationProgress.promptsUsed.length > 0 && (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 max-w-lg mx-auto mb-4">
                    <div className="flex items-center justify-center gap-2 text-sm">
                      <FileText className="w-4 h-4 text-slate-500" />
                      <span className="text-slate-600">Using Prompt Template:</span>
                      {generationProgress.promptsUsed.map((prompt, idx) => (
                        <Badge key={idx} className="bg-blue-100 text-blue-700 border-blue-200">
                          {prompt.reference} ({prompt.framework})
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Progress Bar */}
                <div className="max-w-lg mx-auto mb-6">
                  <div className="flex items-center justify-between text-sm text-slate-600 mb-2">
                    <span>Progress</span>
                    <span>{generationProgress.processedCategories.length} of {generationProgress.totalCategories} categories</span>
                  </div>
                  <Progress 
                    value={(generationProgress.processedCategories.length / generationProgress.totalCategories) * 100} 
                    className="h-3"
                  />
                </div>
                
                {/* Currently active categories (concurrent) */}
                {(() => {
                  const active = (generationProgress.categories || []).filter(
                    c => (generationProgress.categoryResults[c.name] || {}).status === 'generating'
                  );
                  if (active.length === 0) return null;
                  return (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 max-w-lg mx-auto mb-6" data-testid="wizard-active-categories">
                      <div className="flex items-center gap-3">
                        <Loader2 className="w-5 h-5 text-emerald-600 animate-spin shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-emerald-600 font-medium">
                            Currently generating{active.length > 1 ? ` ${active.length} categories` : ''}
                          </p>
                          <p className="text-slate-900 font-semibold truncate">
                            {active.map(c => c.name).join(', ')}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })()}
                
                {/* Running Total */}
                <div className="text-center mb-6">
                  <p className="text-3xl font-bold text-emerald-600">{generationProgress.totalControlsGenerated}</p>
                  <p className="text-sm text-slate-600">Controls Generated</p>
                </div>
                
                {/* Categories Progress List */}
                <div className="max-w-lg mx-auto">
                  <h4 className="text-sm font-medium text-slate-700 mb-3">Category Progress</h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {generationProgress.categories.map((cat, idx) => {
                      const result = generationProgress.categoryResults[cat.name] || {};
                      const status = result.status
                        || (generationProgress.processedCategories.includes(cat.name) ? 'done' : 'pending');
                      const isGenerating = status === 'generating';
                      const isDone = status === 'done';
                      const isFailed = status === 'failed';

                      return (
                        <div 
                          key={cat.name}
                          className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                            isGenerating 
                              ? 'bg-emerald-50 border-emerald-300' 
                              : isDone 
                                ? 'bg-green-50 border-green-200' 
                                : isFailed
                                  ? 'bg-red-50 border-red-200'
                                  : 'bg-slate-50 border-slate-200'
                          }`}
                          data-testid={`wizard-category-row-${idx}`}
                        >
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                            isGenerating 
                              ? 'bg-emerald-200' 
                              : isDone 
                                ? 'bg-green-200' 
                                : isFailed
                                  ? 'bg-red-200'
                                  : 'bg-slate-200'
                          }`}>
                            {isGenerating ? (
                              <Loader2 className="w-4 h-4 text-emerald-600 animate-spin" />
                            ) : isDone ? (
                              <Check className="w-4 h-4 text-green-600" />
                            ) : isFailed ? (
                              <AlertCircle className="w-4 h-4 text-red-600" />
                            ) : (
                              <span className="text-xs text-slate-500">{idx + 1}</span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium truncate ${
                              isGenerating ? 'text-emerald-700' : isDone ? 'text-green-700' : isFailed ? 'text-red-700' : 'text-slate-600'
                            }`}>
                              {cat.name}
                            </p>
                            <p className="text-xs text-slate-500">{cat.framework}</p>
                          </div>
                          {isGenerating && (
                            <Badge className="bg-emerald-100 text-emerald-700">Generating…</Badge>
                          )}
                          {isDone && (
                            <Badge className="bg-green-100 text-green-700">+{result.controls || 0}</Badge>
                          )}
                          {isFailed && (
                            <Badge className="bg-red-100 text-red-700">Error</Badge>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
            
            {/* Deduplicating Phase */}
            {generationProgress.phase === 'deduplicating' && (
              <div className="text-center py-8" data-testid="wizard-dedup-phase">
                <div className="w-20 h-20 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Sparkles className="w-10 h-10 text-amber-600 animate-pulse" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Deduplicating Controls</h2>
                <p className="text-slate-600 mb-1">Pass 1: Within-framework duplicates &middot; Pass 2: Cross-framework matching</p>
                <p className="text-3xl font-mono font-bold text-amber-600 mt-3">
                  {generationProgress.dedupElapsed}s
                </p>
              </div>
            )}
            
            {/* Finalizing Phase */}
            {generationProgress.phase === 'finalizing' && (
              <div className="text-center py-8">
                <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Finalizing</h2>
                <p className="text-slate-600">Completing audit setup...</p>
              </div>
            )}
            
            {/* Complete Phase */}
            {generationProgress.phase === 'complete' && (
              <div className="text-center py-8">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-green-700 mb-2">Audit Created Successfully!</h2>
                <p className="text-slate-600 mb-6">Your audit is ready with AI-generated controls</p>
                
                <div className="flex justify-center gap-8 mb-8">
                  <div className="text-center">
                    <p className="text-4xl font-bold text-emerald-600">{generationProgress.totalControlsGenerated}</p>
                    <p className="text-sm text-slate-600">Controls Created</p>
                  </div>
                  <div className="text-center">
                    <p className="text-4xl font-bold text-blue-600">{generationProgress.processedCategories.length}</p>
                    <p className="text-sm text-slate-600">Categories</p>
                  </div>
                </div>
                
                <Button
                  onClick={goToAudit}
                  className="bg-gradient-to-r from-navy-600 to-navy-700 hover:from-navy-700 hover:to-navy-800 text-white"
                  data-testid="go-to-audit-button"
                >
                  <ArrowRight className="w-4 h-4 mr-2" />
                  Go to Audit
                </Button>
              </div>
            )}
            
            {/* Error Phase */}
            {generationProgress.phase === 'error' && (
              <div className="text-center py-8">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <AlertCircle className="w-10 h-10 text-red-600" />
                </div>
                <h2 className="text-2xl font-bold text-red-700 mb-2">Something Went Wrong</h2>
                <p className="text-slate-600 mb-6">An error occurred while creating your audit</p>
                <Button
                  onClick={() => {
                    setCurrentStep(4);
                    setGenerationProgress({ phase: 'idle', plan: null, categories: [], currentCategory: null, currentCategoryIndex: 0, totalCategories: 0, processedCategories: [], categoryResults: {}, totalControlsGenerated: 0 });
                  }}
                  variant="outline"
                  className="mr-2"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Go Back
                </Button>
                <Button
                  onClick={startControlGeneration}
                  className="bg-navy-600 hover:bg-navy-700"
                >
                  Try Again
                </Button>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-appbg">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="flex items-center space-x-2"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Dashboard</span>
            </Button>
            
            <div>
              <h1 className="text-xl font-bold text-slate-900">Create New Audit</h1>
              <p className="text-sm text-slate-600">Step {currentStep} of {totalSteps}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white/50 border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center space-x-4">
            {Array.from({ length: totalSteps }).map((_, index) => {
              const stepNumber = index + 1;
              const isCompleted = stepNumber < currentStep;
              const isCurrent = stepNumber === currentStep;
              
              return (
                <div key={stepNumber} className="flex items-center">
                  <div 
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                      isCompleted 
                        ? 'bg-navy-600 text-white' 
                        : isCurrent 
                        ? 'bg-emerald-100 text-navy-700 border-2 border-navy-600'
                        : 'bg-slate-200 text-slate-500'
                    }`}
                  >
                    {isCompleted ? <CheckCircle className="w-5 h-5" /> : stepNumber}
                  </div>
                  
                  {index < totalSteps - 1 && (
                    <div 
                      className={`w-12 h-1 mx-2 rounded-full transition-colors ${
                        isCompleted ? 'bg-navy-600' : 'bg-slate-200'
                      }`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-sm">
          <CardContent className="p-8">
            {renderStepContent()}
            
            {/* Navigation - Hide during generation (step 5) */}
            {currentStep < 5 && (
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200">
                <Button
                  variant="outline"
                  onClick={prevStep}
                  disabled={currentStep === 1}
                  className="flex items-center space-x-2"
                  data-testid="previous-step"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Previous</span>
                </Button>

                {currentStep === 4 ? (
                  <Button
                    onClick={handleSubmit}
                    disabled={!canProceed() || loading}
                    className="bg-gradient-to-r from-navy-600 to-navy-700 hover:from-navy-700 hover:to-navy-800 text-white flex items-center space-x-2"
                    data-testid="create-audit-button"
                  >
                    {loading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        <span>Creating...</span>
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4" />
                        <span>Create Audit</span>
                      </>
                    )}
                  </Button>
                ) : (
                  <Button
                    onClick={nextStep}
                    disabled={!canProceed()}
                    className="bg-navy-600 hover:bg-navy-700 text-white flex items-center space-x-2"
                    data-testid="next-step"
                  >
                    <span>Next</span>
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AuditWizard;